import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader, Button, FormGroup, Label, Input } from "reactstrap";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { get, del, put } from "../../helpers/api_helper";
import { Link, useNavigate } from "react-router-dom";
import { TRANSACTION_LIST, DELETE_TRANSACTION, UPDATE_TRANSACTION } from "../../helpers/url_helper";
import transactionColumns from "../../components/Columns/TransactionColumns";
import toastr from "toastr";
import "toastr/build/toastr.min.css"; // Make sure this is imported for styling
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";


const Transaction = () => {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const role = Number(authUser?.user?.role);
      setIsAdmin(role === 1);
    } catch {
      setIsAdmin(false);
    }
  }, []);
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Transaction", link: "#" },
  ];

  const [transactions, setTransactions] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);  // Modal state
  const [selectedTransaction, setSelectedTransaction] = useState(null);  // Store the collection to be deleted
  const [editData, setEditData] = useState({});
  const [errors, setErrors] = useState({});
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const navigate = useNavigate();

  const getTransactionList = async (page, size, sort = [], search = "") => {
    const sortKey = sort[0]?.id;
    const sortby = sort[0]?.desc ? "asc" : "desc";

    return await get(TRANSACTION_LIST, {
      params: { page, size, sortKey, sortby, search },
    });
  };
 

const fetchTransaction = async (page = 1, size = 10, sortBy = [], searchText = "") => {
  try {
      const response = await getTransactionList(page, size, sortBy, searchText); 
      const { items, totalPages } = response.data;
      
      setTransactions(items);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
};



// Now it's safe to use in useEffect
useEffect(() => {
  fetchTransaction(pageIndex, pageSize, sortBy, searchText);
}, [pageIndex, pageSize, sortBy, searchText]);


  const handleSearchChange = (text) => {
    setPageIndex(1);
    setSearchText(text);
  }; 

  const handleEdit = (item) => {
    setEditData({
      ...item,
      status: item.transaction_status || "",
      notes: item.notes || "",
    });
    setEditModalOpen(true);
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: name === "status" ? Number(value) : value,
    }));
  };

  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  // Open the delete confirmation modal
  const handleDelete = (item) => {
    setSelectedTransaction(item);  // Set the collection to be deleted
    setModalOpen(true);  // Open the modal
  };

  // Close the modal
  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleUpdate = async () => {
    const newErrors = {};

    if (!editData.status) {
      newErrors.status = "Status is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return; // stop if validation fails
    }

    setErrors({}); // clear previous errors if any

    const payload = {
      status: editData.status,
      notes: editData.notes,
    };

    try {
      const response = await put(UPDATE_TRANSACTION(editData.ul_id), payload);
      toastr.success(response.message || "Transaction updated successfully!");
      fetchTransaction(pageIndex, pageSize, sortBy, searchText);
      setEditModalOpen(false);
    } catch (error) {
      toastr.error("Something went wrong while updating.");
    }
  };
  // Confirm deletion
  const confirmDelete = async () => {
    try {
      if (selectedTransaction) {
        await del(DELETE_TRANSACTION(selectedTransaction.ul_id)); // Call delete API
        toastr.success("Transaction deleted successfully!");
        fetchTransaction(pageIndex, pageSize, sortBy, searchText);  // Refresh the collection list
      }
    } catch (error) {
      console.error("Failed to delete collection:", error.response?.data || error.message);
      toastr.error("Something went wrong while deleting.");
    }
    setModalOpen(false);  // Close the modal after the operation
  };
 

  const filteredTransactions = useMemo(() => {
    if (!fromDate || !toDate) return transactions;

    const from = new Date(fromDate);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999); // Include the whole day

    return transactions.filter(tx => {
      const txDate = new Date(tx.received_date); // or tx.createdAt
      return txDate >= from && txDate <= to && tx.transaction_status === 1;
    });
  }, [transactions, fromDate, toDate]);
  

  const { incomeTotal, expenseTotal, otherTotal } = useMemo(() => {
    const list = fromDate && toDate ? filteredTransactions : transactions;
    return list.reduce(
      (acc, tx) => {
        const type = Number(tx.transaction_type);
        const amount = Number(tx.amount || 0);
        if (tx.transaction_status !== 1) return acc; // Skip this transaction
        if (type === 1) acc.incomeTotal += amount;
        else if (type === 2) acc.expenseTotal += amount;
        else acc.otherTotal += amount;
        return acc;
      },
      { incomeTotal: 0, expenseTotal: 0, otherTotal: 0 }
    );
  }, [transactions, filteredTransactions, fromDate, toDate]);


  const columns = useMemo(() => transactionColumns(handleEdit, handleDelete), []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Transaction" breadcrumbItems={breadcrumbItems} />
        </Container>
        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : transactions.length > 0 ? (
              <TableContainer
                columns={columns || []}
                data={transactions || []}
                isPagination={true}
                isGlobalFilter={true}
                customPageSize={pageSize}
                isAddOptions={true}
                addOptionTitle={"Add New Transaction"}
                iscustomPageSize={false}
                isBordered={false}
                controlledPagination={{
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                  totalPages,
                }}
                onSortChange={setSortBy}
                searchText={searchText}
                onSearchChange={handleSearchChange}
                handleAddClick={() => {
                  navigate("/add-transaction");
                }}
              />
            ) : (
              <div className="text-center py-5">
                <h5>No Record Found</h5>
                {isAdmin && (
                  <Link to="/add-transaction" className="btn btn-primary mt-3"> Add New Transaction </Link>
                )}

              </div>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardBody>
          <div className="d-md-flex d-block align-items-end gap-3 mb-4">
            <h4>Filter by Date</h4>
            <div className="flex-1 d-sm-flex d-block">
              <div style={{maxWidth: "200px"}}>
                <Flatpickr
                  options={{ dateFormat: "Y-m-d" }}
                  value={fromDate} 
                  onChange={(selectedDates) => {
                    if (selectedDates?.[0]) {
                      // ✅ Correct local date format
                      const formatted = selectedDates[0].toLocaleDateString("en-CA"); // "YYYY-MM-DD"
                      setFromDate(formatted);
                    }
                  }}
                  className="form-control"
                  placeholder="From Date"
                  />
                </div>
                <div style={{maxWidth: "200px"}}>
                <Flatpickr
                  options={{ dateFormat: "Y-m-d" }}
                  value={toDate}
                  onChange={(selectedDates) => {
                    if (selectedDates?.[0]) {
                      // ✅ Correct local date format
                      const formatted = selectedDates[0].toLocaleDateString("en-CA"); // "YYYY-MM-DD"
                      setToDate(formatted);
                    }
                  }}
                  className="form-control"
                  placeholder="To Date"
                />
              </div>
            </div>
             <div className="mt-4">
              <div className="align-items-end gap-3 d-sm-flex d-block">
                <h4 className="mb-0"><strong>Income:</strong> <label className="badge bg-success"> ₹{incomeTotal.toFixed(2)}</label></h4>
                <h4 className="mb-0"><strong>Expense:</strong><label className="badge bg-primary"> ₹{expenseTotal.toFixed(2)}</label></h4>
                <h4 className="mb-0"><strong>Other:</strong> <label className="badge bg-info">₹{otherTotal.toFixed(2)} </label></h4>
              </div>
          </div>
          </div>
          </CardBody>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this Transaction permanently?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </ModalFooter>
      </Modal>
      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Transaction</ModalHeader>
        <ModalBody>
          {/* <Form> */}
          <FormGroup>
            <Label>Title</Label>
            <h6><strong>{editData.title}</strong></h6>
          </FormGroup>
          <FormGroup>
            <Label>Amount </Label>
            <h6><strong>{editData.amount}</strong></h6>
          </FormGroup>
          <FormGroup>
            <Label>Change Status</Label>
            <Input
              type="select"
              name="status"
              value={editData.status ?? ""}
              onChange={handleEditChange}
              invalid={!!errors.status}
            >
              <option readonly value="">Select status</option>
              <option value={1}>Approved</option>
              <option value={2}>Cancelled</option>
            </Input>
            {errors.status && (
              <div className="invalid-feedback d-block">{errors.status}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Remark </Label>
            <Input
              type="textarea"
              name="notes"
              value={editData.notes ?? ""}
              onChange={handleEditChange}
            >
            </Input>
          </FormGroup>
          {/* Add other fields as needed */}
          {/* </Form> */}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>Cancel</Button>
          <Button color="primary" onClick={handleUpdate}>Update</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Transaction;
